//#region Imports
import * as React from "react";
import type { AnalyticsEventsTypesMapT } from "@anydo/analytics";
import { usePreviousValue } from "@anydo/ui-react-utils";
import { useAnalytics } from "./useAnalytics";
//#endregion

//#region Types
type EventsT = keyof AnalyticsEventsTypesMapT;

type ReturnT<Event extends EventsT> = AnalyticsEventsTypesMapT[Event] extends void
  ? () => void
  : (options: AnalyticsEventsTypesMapT[Event]) => void;
//#endregion

//#region Functions
const useValidation = __DEV__
  ? function (event: string) {
      const prevEvent = usePreviousValue(event);

      if (event !== prevEvent) {
        throw new Error(
          `useAnalyticsTrackOnce(): You can only use this hook for one event. Used both for: '${prevEvent}' and '${event}'`,
        );
      }
    }
  : function () {};
//#endregion

export function useAnalyticsTrackOnce<Event extends EventsT>(event: Event): ReturnT<Event> {
  // Making sure "event" isn't changes, otherwise, we cannot gurrent only one run
  useValidation(event);

  const { track } = useAnalytics();
  const ranRef = React.useRef(false);

  const trackOnce = React.useCallback(function trackOnce(options: any): void {
    if (ranRef.current) return;
    ranRef.current = true;

    // @ts-expect-error Limitation of TS typing system
    track<Event>(event, options);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return trackOnce as ReturnT<Event>;
}
